"use client";

import "./tabs.css";
import { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { NB_TAB_SWITCH } from "@/lib/notebook/core/notebook-motion-presets";
import { Plus, X } from "lucide-react";
import { TabRenameInput } from "@/lib/notebook/components/TabRenameInput";
import { stripHtmlToPlainText } from "@/lib/notebook/text/plain-text-value";
import { usePersistedBlock } from "@/lib/notebook/core/hooks/use-persisted-block";
import {
  MAX_NOTEBOOK_TABS,
  createTab,
  clampTabTitle,
  normalizeTabsBlockData,
  TAB_TITLE_MAX_LENGTH,
  tabHasContent,
} from "@/lib/notebook/models/tabs-model";
import TabChildBlock from "./TabChildBlock";

const TABS_DEFAULTS_REF = { current: null };

function getTabsDefaults() {
  if (!TABS_DEFAULTS_REF.current) {
    TABS_DEFAULTS_REF.current = normalizeTabsBlockData(null);
  }
  return TABS_DEFAULTS_REF.current;
}

export function Tabs({
  data,
  onChange,
  locked,
  stripOnly = false,
  onUpdateChildBlock,
  onRemoveChildBlock,
  onRemoveSection,
  onSelectTab: onSelectTabExternal,
  onAddTab: onAddTabExternal,
  onDeleteTab: onDeleteTabExternal,
  hasTabContent: hasTabContentExternal,
}) {
  const { state, commit } = usePersistedBlock(data, onChange, {
    defaults: getTabsDefaults(),
  });
  const tabsData = useMemo(() => normalizeTabsBlockData(state), [state]);
  const scrollRef = useRef(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const tabLimit = MAX_NOTEBOOK_TABS;
  const canAddTab = !locked && tabsData.tabs.length < tabLimit;

  const activeTab = tabsData.tabs.find((t) => t.id === tabsData.activeTab);
  const activeBlocks = activeTab?.blocks ?? [];

  const persist = useCallback(
    (patch) => {
      if (locked) return;
      commit((prev) =>
        normalizeTabsBlockData({ ...normalizeTabsBlockData(prev), ...patch }),
      );
    },
    [commit, locked],
  );

  const selectTab = (id) => {
    if (editingTabId && editingTabId !== id) {
      setEditingTabId(null);
    }
    if (onSelectTabExternal) {
      onSelectTabExternal(id);
      persist({ activeTab: id });
      return;
    }
    persist({ activeTab: id });
  };

  const updateLabel = (id, label) => {
    persist({
      tabs: tabsData.tabs.map((t) =>
        t.id === id ? { ...t, label: clampTabTitle(label ?? '') } : t,
      ),
    });
  };

  const addTab = () => {
    if (!canAddTab) return;
    if (onAddTabExternal) {
      onAddTabExternal();
      return;
    }
    const tab = createTab(`Tab ${tabsData.tabs.length + 1}`);
    persist({
      tabs: [...tabsData.tabs, tab],
      activeTab: tab.id,
    });
  };

  const tabHasAnyContent = (tabId) => {
    if (hasTabContentExternal) return hasTabContentExternal(tabId);
    return tabHasContent(tabsData, tabId);
  };

  const deleteTab = (tabId) => {
    if (tabsData.tabs.length <= 1) return;
    if (onDeleteTabExternal) {
      onDeleteTabExternal(tabId);
      setPendingDeleteId(null);
      return;
    }
    const nextTabs = tabsData.tabs.filter((t) => t.id !== tabId);
    const nextActive =
      tabsData.activeTab === tabId ? nextTabs[0].id : tabsData.activeTab;
    persist({ tabs: nextTabs, activeTab: nextActive });
    setPendingDeleteId(null);
  };

  const requestDeleteTab = (tabId) => {
    if (tabsData.tabs.length <= 1 || locked) return;
    if (tabHasAnyContent(tabId)) {
      setPendingDeleteId(tabId);
      return;
    }
    deleteTab(tabId);
  };

  return (
    <div className="tabs-block" data-block-type="block_tabs">
      <div className="tabs-strip">
        <div
          className="tabs tabs-scroll"
          ref={scrollRef}
          role="tablist"
        >
          {tabsData.tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={tabsData.activeTab === tab.id}
              className={`tab ${tabsData.activeTab === tab.id ? "active" : ""}`}
              onClick={() => selectTab(tab.id)}
              onDoubleClick={(e) => {
                if (locked) return;
                e.preventDefault();
                e.stopPropagation();
                selectTab(tab.id);
                setEditingTabId(tab.id);
              }}
              title={
                locked
                  ? stripHtmlToPlainText(tab.label)
                  : `${stripHtmlToPlainText(tab.label)} — double-click to rename`
              }
            >
              <div
                className="tab-label"
                data-nb-tab-rename={editingTabId === tab.id ? "" : undefined}
                onClick={(e) => {
                  if (editingTabId === tab.id) e.stopPropagation();
                }}
              >
                {!locked && editingTabId === tab.id ? (
                  <TabRenameInput
                    className="tab-rename-input"
                    value={tab.label}
                    maxLength={TAB_TITLE_MAX_LENGTH}
                    onCommit={(val) => {
                      updateLabel(tab.id, val);
                      setEditingTabId(null);
                    }}
                    onCancel={() => setEditingTabId(null)}
                  />
                ) : (
                  <span className="tab-label-display">
                    {stripHtmlToPlainText(tab.label) || "Tab"}
                  </span>
                )}
              </div>
              {!locked && tabsData.tabs.length > 1 && (
                <span
                  role="button"
                  tabIndex={0}
                  className="tab-remove"
                  aria-label={`Remove ${tab.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    requestDeleteTab(tab.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      requestDeleteTab(tab.id);
                    }
                  }}
                >
                  <X size={9} strokeWidth={2} />
                </span>
              )}
            </button>
          ))}
          {canAddTab && (
            <button
              type="button"
              className="tab tab--add"
              onClick={addTab}
              aria-label="Add tab"
            >
              <Plus size={11} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {pendingDeleteId && (
        <div className="tabs-delete-confirm" role="alertdialog">
          <p>
            Delete this tab? All blocks and content inside it will be
            permanently removed.
          </p>
          <div className="tabs-delete-confirm__actions">
            <button type="button" onClick={() => setPendingDeleteId(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => deleteTab(pendingDeleteId)}
            >
              Delete tab
            </button>
          </div>
        </div>
      )}

      {!stripOnly && activeTab && (
        <motion.div
          key={activeTab.id}
          role="tabpanel"
          className="tabs-panel-view"
          aria-labelledby={activeTab.id}
          initial={NB_TAB_SWITCH.initial}
          animate={NB_TAB_SWITCH.animate}
          transition={NB_TAB_SWITCH.transition}
        >
          {activeBlocks.length === 0 ? (
            <div className="tabs-empty">
              <p>Empty view — use the + beside this block to add content.</p>
            </div>
          ) : (
            <div className="tabs-panel-blocks">
              {activeBlocks.map((block) => (
                <TabChildBlock
                  key={block.id}
                  block={block}
                  locked={locked}
                  onChange={(d) =>
                    onUpdateChildBlock?.(activeTab.id, block.id, d)
                  }
                  onRemove={() => onRemoveChildBlock?.(activeTab.id, block.id)}
                />
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
